import { AlertTriangle, CheckCircle, Clock, Loader2, Video } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import { JobPostingsList } from '../components/job-postings/JobPostingsList';
import { KSLogo } from '../components/ui/KSLogo';
import { useApplicationStore, type ExistingApplication } from '../store/applicationStore';
import { useAuthStore } from '../store/authStore';

export function LandingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const checkExistingApplications = useApplicationStore((state) => state.checkExistingApplications);
  const jobPostingId = useApplicationStore((state) => state.jobPostingId);
  const serverApplicationId = useApplicationStore((state) => state.serverApplicationId);

  const [isCheckingApplication, setIsCheckingApplication] = useState(false);
  const [submittedApplication, setSubmittedApplication] = useState<ExistingApplication | null>(null);
  const [activeAppCount, setActiveAppCount] = useState(0);
  const isLimitReached = activeAppCount >= 5;

  // Check for existing submitted applications when authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      setSubmittedApplication(null);
      setActiveAppCount(0);
      return;
    }

    setIsCheckingApplication(true);
    checkExistingApplications()
      .then((applications) => {
        const submitted = applications.find((app) => app.status === 'submitted');
        setSubmittedApplication(submitted ?? null);
        setActiveAppCount(applications.filter((a) => a.status !== 'rejected').length);
      })
      .finally(() => {
        setIsCheckingApplication(false);
      });
  }, [isAuthenticated, checkExistingApplications]);

  const handleViewApplication = () => {
    if (submittedApplication) {
      void navigate(`/dashboard?applicationId=${encodeURIComponent(submittedApplication.applicationId)}`);
    }
  };

  return (
    <div className='flex flex-col min-h-[90vh] bg-brand-pale'>
      {/* Hero Section */}
      <div className='flex-1 flex flex-col items-center justify-center px-4 pt-8 pb-12 text-center max-w-4xl mx-auto'>
        <div className='mb-5 transform hover:scale-105 transition duration-500'>
          <KSLogo className='h-24 w-auto text-primary' />
        </div>

        <h1 className='text-4xl md:text-5xl font-extrabold text-brand-gray mb-6 tracking-tight leading-tight'>
          {t('landing.hero.title')}
        </h1>

        <p className='text-lg md:text-xl text-gray-600 mb-4 max-w-3xl leading-relaxed'>
          {t('landing.hero.description')}
        </p>

        <p className='text-lg md:text-xl text-gray-600 mb-8 max-w-3xl leading-relaxed'>
          {t('landing.hero.description2')}
        </p>

        {/* What to Expect */}
        <div className='flex flex-col sm:flex-row gap-4 mb-8 w-full max-w-xl'>
          <div className='flex-1 flex items-center gap-3 bg-white rounded-lg p-4 shadow-sm border border-gray-100'>
            <CheckCircle className='h-6 w-6 text-primary shrink-0' />
            <span className='text-base font-medium text-gray-700'>{t('landing.whatToExpect.questions')}</span>
          </div>
          <div className='flex-1 flex items-center gap-3 bg-white rounded-lg p-4 shadow-sm border border-gray-100'>
            <Video className='h-6 w-6 text-primary shrink-0' />
            <span className='text-base font-medium text-gray-700'>{t('landing.whatToExpect.recorded')}</span>
          </div>
          <div className='flex-1 flex items-center gap-3 bg-white rounded-lg p-4 shadow-sm border border-gray-100'>
            <Clock className='h-6 w-6 text-primary shrink-0' />
            <span className='text-base font-medium text-gray-700'>{t('landing.whatToExpect.duration')}</span>
          </div>
        </div>

        {/* CTA */}
        <div className='flex flex-col sm:flex-row gap-3 w-full justify-center max-w-lg'>
          {isAuthenticated ? (
            isCheckingApplication ? (
              <div
                role='status'
                aria-label={t('common.loading')}
                className='flex-1 flex items-center justify-center px-10 py-4 bg-primary/70 text-white text-xl font-bold rounded-lg'>
                <Loader2 className='h-6 w-6 animate-spin' aria-hidden='true' />
                <span className='sr-only'>{t('common.loading')}</span>
              </div>
            ) : submittedApplication ? (
              <button
                type='button'
                onClick={handleViewApplication}
                className='flex-1 flex items-center justify-center px-10 py-4 bg-primary text-white text-xl font-bold rounded-lg hover:bg-primary-dark transition shadow-lg shadow-primary/30'>
                {t('landing.hero.cta.view')}
              </button>
            ) : jobPostingId && serverApplicationId ? (
              <Link
                to='/wizard'
                className='flex-1 flex items-center justify-center px-10 py-4 bg-primary text-white text-xl font-bold rounded-lg hover:bg-primary-dark transition shadow-lg shadow-primary/30'>
                {t('landing.hero.cta.continue')}
              </Link>
            ) : (
              <Link
                to='/jobs'
                className='flex-1 flex items-center justify-center px-10 py-4 bg-primary text-white text-xl font-bold rounded-lg hover:bg-primary-dark transition shadow-lg shadow-primary/30'>
                {t('landing.hero.cta.browse')}
              </Link>
            )
          ) : (
            <>
              <Link
                to='/auth'
                className='flex-1 flex items-center justify-center px-10 py-4 bg-primary text-white text-xl font-bold rounded-lg hover:bg-primary-dark transition shadow-lg shadow-primary/30'>
                {t('landing.hero.cta.apply')}
              </Link>
              <Link
                to='/auth'
                className='flex-1 flex items-center justify-center px-10 py-4 bg-white text-brand-gray border border-gray-200 text-xl font-bold rounded-lg hover:bg-gray-50 transition shadow-sm'>
                {t('landing.hero.cta.status')}
              </Link>
            </>
          )}
        </div>
      </div>

      {/* Job Postings Section */}
      <div className='bg-white py-10 px-4'>
        <div className='max-w-6xl mx-auto'>
          <div className='text-center mb-6'>
            <h2 className='text-3xl md:text-4xl font-bold text-brand-gray mb-2'>{t('jobPostings.sectionTitle')}</h2>
            <p className='text-gray-600'>{t('jobPostings.sectionSubtitle')}</p>
          </div>
          {isAuthenticated && isLimitReached && (
            <div className='mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4'>
              <div className='flex items-center gap-2 text-amber-700'>
                <AlertTriangle className='h-5 w-5 shrink-0' />
                <span className='font-semibold'>{t('jobPostings.applicationLimit.banner')}</span>
              </div>
              <p className='mt-1 text-sm text-amber-600 ml-7'>
                <Link to='/dashboard' className='font-medium text-primary hover:text-primary-dark underline'>
                  {t('jobPostings.applicationLimit.bannerLink')}
                </Link>
              </p>
            </div>
          )}
          <JobPostingsList limit={6} />
          <div className='text-center mt-6'>
            <Link
              to='/jobs'
              className='inline-flex items-center gap-1 text-primary font-semibold hover:text-primary-dark transition'>
              {t('jobPostings.viewAll')}
              <span aria-hidden='true'>&rarr;</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className='bg-brand-gray text-gray-400 py-6 text-center text-sm'>
        <div className='flex justify-center mb-4 gap-4'>
          <a href='/privacy-policy.html' className='hover:text-white transition underline-offset-2 hover:underline'>
            {t('landing.footer.privacy')}
          </a>
          <a href='/sms-terms.html' className='hover:text-white transition underline-offset-2 hover:underline'>
            {t('landing.footer.smsTerms')}
          </a>
        </div>
        <p>
          © {currentYear} {t('landing.footer.copyright')}
        </p>
      </div>
    </div>
  );
}
